import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import { LockKeyhole, Pencil } from 'lucide-react'
import Card from 'app/frontend/base/components/Card'
import CharacterClassIcon from 'app/frontend/base/components/CharacterClassIcon'
import EventStatusIcon from 'app/frontend/base/components/EventStatusIcon'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { useTeam } from 'app/frontend/base/config/context/team'
import { displayErrorToast } from 'app/frontend/base/helpers'
import CharacterRole from 'app/frontend/types/custom/CharacterRole'
import { updateEventParticipantSignup } from 'app/frontend/base/api/event'
import { classColours } from 'app/frontend/types/custom/CharacterClass'
import { unavailableParticipantStatuses } from 'app/frontend/types/custom/EventParticipantStatus'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import useApplyEventUpdate from '../hooks/useApplyEventUpdate'
import { EventActivityParticipantEntry } from './eventActivityParticipantEntry'
import ViewerParticipantActivitySummary from './ViewerParticipantActivitySummary'
import { getViewerSelectableCharacters } from './viewerSignupShared'
import { toast } from 'sonner'
import { EventActivityLinkedPlanData } from '../hooks/useEventActivityLinkedPlanData'
import EventParticipantEditPopover from './EventParticipantEditPopover'
import ViewerAssignmentsButton from './ViewerAssignmentsButton'

type Props = {
  event: wowaudit.Event
  entries: EventActivityParticipantEntry[]
  activeActivities: wowaudit.EventActivity[]
  linkedPlanDataByActivityId: Map<number, EventActivityLinkedPlanData>
  selectableStatuses: wowaudit.EventParticipantReference['status'][]
  focusActivity?: wowaudit.EventActivity
}

const ExistingParticipantSection = ({
  entry,
  activeActivities,
  linkedPlanDataByActivityId,
  selectableStatuses,
  isSaving,
  isLocked,
  selectableCharacters,
  isDetailed,
  focusActivity,
  onSave,
}: {
  entry: EventActivityParticipantEntry
  activeActivities: wowaudit.EventActivity[]
  linkedPlanDataByActivityId: Map<number, EventActivityLinkedPlanData>
  selectableStatuses: wowaudit.EventParticipantReference['status'][]
  isSaving: boolean
  isLocked: boolean
  selectableCharacters: wowaudit.CharacterReference[]
  isDetailed: boolean
  focusActivity?: wowaudit.EventActivity
  onSave: (
    participantId: number,
    status: wowaudit.EventParticipantReference['status'],
    comment: string,
    characterId?: number,
    role?: CharacterRole,
  ) => void
}) => {
  const { t } = useTranslation()
  const character = entry.participant.primaryCharacterReference
  const [editorOpen, setEditorOpen] = useState(false)
  const classColoursForCharacter = classColours[character.characterClass]
  const canChangeCharacter =
    entry.participant.addedAsGuest && selectableCharacters.length > 0

  const editPopover = isLocked ? (
    <Badge variant='warning' className='h-6 px-2 gap-1.5'>
      <LockKeyhole className='w-3.5 h-3.5' />
      Locked
    </Badge>
  ) : (
    <EventParticipantEditPopover
      open={editorOpen}
      onOpenChange={setEditorOpen}
      trigger={
        <Button
          variant='outline'
          disabled={isSaving}
          size='custom'
          className='flex items-center gap-2 py-0.5 px-2'
        >
          <Pencil className='w-3.5 h-3.5' />
          Edit
        </Button>
      }
      selectableStatuses={selectableStatuses}
      currentStatus={entry.participant.status}
      currentComment={entry.participant.comment}
      isSaving={isSaving}
      canChangeCharacter={canChangeCharacter}
      selectableCharacters={selectableCharacters}
      currentCharacterId={entry.participant.characterId.toString()}
      currentRole={entry.participant.role as CharacterRole}
      onSave={(status, comment, characterId, role) =>
        onSave(entry.participant.id, status, comment, characterId, role)
      }
    />
  )

  if (!isDetailed) {
    const singleActivity = focusActivity ?? activeActivities[0]
    const singleSelection = singleActivity
      ? entry.selections[singleActivity.id]
      : undefined
    const isUnavailable = unavailableParticipantStatuses.includes(
      entry.participant.status,
    )
    const isSelected = !isUnavailable && !!singleSelection?.selected

    return (
      <div className='flex items-center gap-2 flex-wrap'>
        <div
          className={cn(
            'flex items-center gap-1.5',
            classColoursForCharacter.text,
          )}
        >
          <CharacterClassIcon characterClass={character.characterClass} />
          <span>{character.name}</span>
        </div>

        <Badge variant='outline' className='flex items-center gap-2'>
          <EventStatusIcon status={entry.participant.status} size='small' />
          <span className='text-sm'>
            {t(
              `settings.raidsAvailableStatuses.options.${entry.participant.status}`,
            )}
          </span>
        </Badge>

        {singleSelection && (
          <>
            <Badge variant={isSelected ? 'success' : 'warning'} className='h-6'>
              {isSelected ? 'Selected' : 'Benched'}
            </Badge>
            <Separator orientation='vertical' className='h-6' />
          </>
        )}

        {editPopover}
      </div>
    )
  }

  return (
    <div className='flex gap-3 max-sm:flex-col'>
      <Card className='space-y-2 min-w-34'>
        <div
          className={cn(
            'flex items-center justify-center gap-1.5',
            classColoursForCharacter.text,
          )}
        >
          <CharacterClassIcon characterClass={character.characterClass} />
          <span>{character.name}</span>
        </div>

        <div className='flex flex-col gap-2 max-sm:flex-row max-sm:items-center'>
          <Badge
            variant='outline'
            className='flex items-center justify-center gap-2 max-sm:flex-1'
          >
            <EventStatusIcon status={entry.participant.status} size='small' />
            <span className='text-sm'>
              {t(
                `settings.raidsAvailableStatuses.options.${entry.participant.status}`,
              )}
            </span>
          </Badge>

          <div className='max-sm:flex-1 *:w-full *:justify-center'>
            {editPopover}
          </div>
        </div>
      </Card>

      <div className='self-stretch grow'>
        <ViewerParticipantActivitySummary
          entry={entry}
          activeActivities={activeActivities}
          linkedPlanDataByActivityId={linkedPlanDataByActivityId}
        />
      </div>
    </div>
  )
}

const ViewerExistingParticipantsCard = ({
  entries,
  event,
  activeActivities,
  linkedPlanDataByActivityId,
  selectableStatuses,
  focusActivity,
}: Props) => {
  const { team } = useTeam()
  const { t } = useTranslation()
  const {
    data: { user },
  } = usePageInfo()
  const applyEventUpdate = useApplyEventUpdate()
  const [savingParticipantId, setSavingParticipantId] = useState<number>()
  const signupMutation = useMutation({
    mutationFn: async ({
      participantId,
      status,
      comment,
      characterId,
      role,
    }: {
      participantId: number
      status: wowaudit.EventParticipantReference['status']
      comment: string
      characterId?: number
      role?: CharacterRole
    }) => {
      return await updateEventParticipantSignup(team.id, participantId, {
        status,
        comment,
        characterId,
        role,
      })
    },
    onSuccess: (updatedEvent) => {
      applyEventUpdate(updatedEvent)
      setSavingParticipantId(undefined)
      toast.success(t('shared.changes-saved'))
    },
    onError: (error) => {
      setSavingParticipantId(undefined)
      displayErrorToast(error as Error, t)
    },
  })

  const isDetailed =
    event.activityType === 'raids' &&
    event.raidsPlanningScope === 'per_encounter'
  const assignmentActivities =
    event.raidsPlanningScope === 'single' &&
    event.primaryActivityId != null &&
    focusActivity
      ? [focusActivity]
      : activeActivities

  const saveExistingParticipant = (
    participantId: number,
    status: wowaudit.EventParticipantReference['status'],
    comment: string,
    characterId?: number,
    role?: CharacterRole,
  ) => {
    setSavingParticipantId(participantId)
    signupMutation.mutate({ participantId, status, comment, characterId, role })
  }
  const { selectableCharacters } = getViewerSelectableCharacters({
    event,
    team,
    user,
  })

  return (
    <Card
      className={cn(
        'mb-3 w-fit max-w-full',
        isDetailed ? 'pt-2' : 'py-2 flex flex-wrap gap-3',
      )}
    >
      <div
        className={cn(
          'flex flex-wrap items-center justify-between gap-3',
          isDetailed ? 'mb-2' : '',
        )}
      >
        <h3 className='mt-0 mb-0 text-base font-semibold'>
          {t('dashboard.team.events.viewer-signup.title')}
          {isDetailed ? '' : ':'}
        </h3>

        {entries[0] ? (
          <ViewerAssignmentsButton
            entry={entries[0]}
            activeActivities={assignmentActivities}
            linkedPlanDataByActivityId={linkedPlanDataByActivityId}
          />
        ) : null}
      </div>

      <div>
        {entries.map((entry, index) => (
          <div key={entry.participant.id} className='space-y-3'>
            {index > 0 ? <Separator className='mt-3' /> : null}
            <ExistingParticipantSection
              entry={entry}
              activeActivities={activeActivities}
              linkedPlanDataByActivityId={linkedPlanDataByActivityId}
              selectableStatuses={selectableStatuses}
              isSaving={savingParticipantId === entry.participant.id}
              isLocked={event.status === 'locked'}
              selectableCharacters={selectableCharacters}
              isDetailed={isDetailed}
              focusActivity={focusActivity}
              onSave={saveExistingParticipant}
            />
          </div>
        ))}
      </div>
    </Card>
  )
}

export default ViewerExistingParticipantsCard
